import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import Typography from '@mui/material/Typography';

function mapNpsToAngle(value) {
    if (value < -100) value = -100;
    if (value > 100) value = 100;

    if (value <= 70) {
        // Intervalo -100 a 70 → 1/3 do arco
        const ratio = (value + 100) / 170;
        return -Math.PI / 2 + ratio * (Math.PI / 3);
    } else if (value <= 85) {
        // Intervalo 70 a 85 → 1/3 do arco
        const ratio = (value - 70) / 15;
        return -Math.PI / 2 + (Math.PI / 3) + ratio * (Math.PI / 3);
    } else {
        // Intervalo 85 a 100 → 1/3 do arco
        const ratio = (value - 85) / 15;
        return -Math.PI / 2 + (2 * Math.PI / 3) + ratio * (Math.PI / 3);
    }
}

const NPSChart = ({ value, needle, styleProps, titleProps}) => {
    const svgRef = useRef();
    const [dimensions, setDimensions] = useState({ width: 400, height: 200 });

    const getColor = (csat) => {
        if (csat <= 70) return '#d34c4d';
        if (csat <= 85) return '#f78f33';
        return '#3bae3b';
    }

    useEffect(() => {
        const updateDimensions = () => {
            const width = svgRef.current.clientWidth;
            const height = width / 2;
            setDimensions({ width, height });
        };

        updateDimensions();

        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    useEffect(() => {
        const { width, height } = dimensions;
        const radius = Math.min(width, height) / 2;

        d3.select(svgRef.current).selectAll('*').remove();

        const svg = d3.select(svgRef.current)
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${width / 2}, ${height})`);

        const arc = d3.arc()
            .innerRadius(radius * 1.0)
            .outerRadius(radius * 1.6);

        svg.append('path')
            .attr('class', 'background arc')
            .attr('d', arc({ startAngle: -Math.PI, endAngle: Math.PI }))
            .attr('fill', '#e0e0e0');

        


        if (value !== null){
            const filledArc = d3.arc()
                .innerRadius(radius * 1.0)
                .outerRadius(radius * 1.6)
                .startAngle(-Math.PI / 2)
                .endAngle(mapNpsToAngle(value));

            svg.append('path')
                .attr('class', 'arc')
                .attr('d', filledArc())
                .attr('fill', getColor(value));
        }

        if (needle){
        const needleLength = radius * 0.7;
        const needleBase = radius * 0.1;
        const angle = mapNpsToAngle(value) - Math.PI/2; // from -π/2 (left) to π/2 (right)

        // Tip of the needle
        const tipX = needleLength * Math.cos(angle);
        const tipY = needleLength * Math.sin(angle);

        // Base corners (perpendicular to the needle direction)
        const baseLeftX = needleBase * Math.cos(angle + Math.PI / 2);
        const baseLeftY = needleBase * Math.sin(angle + Math.PI / 2);

        const baseRightX = needleBase * Math.cos(angle - Math.PI / 2);
        const baseRightY = needleBase * Math.sin(angle - Math.PI / 2);

        // Path for triangle needle
        const needlePath = `
            M ${baseLeftX} ${baseLeftY}
            L ${baseRightX} ${baseRightY}
            L ${tipX} ${tipY}
            Z
        `;

        svg.append('path')
            .attr('d', needlePath)
            .attr('fill', 'black');

        svg.append('circle')
            .attr('cx', 0)
            .attr('cy', 0)
            .attr('r', needleBase * 1.2)
            .attr('fill', '#333');
        }
        
        svg.append('text')
            .attr('x', 0)
            .attr('y', -25)
            .attr('text-anchor', 'middle')
            .style('font-size', '1.25rem')
            .text(value);

    }, [value, dimensions]);

    return (
        <div style={ styleProps || { width: '100%', padding: '2rem' }}>
            <Typography sx={titleProps || {}} variant="subtitle1">NPS</Typography>
            <svg ref={svgRef} style={{ width: '100%' }}></svg>
        </div>
    );
};

export default NPSChart;
